import type {
  CatalogBillingTokenRates,
  CatalogModel,
  CatalogUnitPricing,
  UnitUsageUnit,
} from '@nadoutong/contracts';
import type { CnyRateDecimals } from '../../components/formatters.ts';
import { formatCnyRateFromUsdRate, formatUsdMicrosAsCny } from '../../components/formatters.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { useT } from '../../i18n/index.ts';
import { trimCnyTrailingZeros } from './trimCnyTrailingZeros.ts';

/**
 * Price-line primitives of the `/pricing` page, extracted verbatim from
 * `Pricing.tsx` (tenant#117) so the self-describing billing renderer in
 * `billing.tsx` can share them without an import cycle. Rendering is
 * unchanged — the legacy vendor matrices and the flat SKU market rows
 * produce byte-identical DOM through these.
 */

type TranslateFn = ReturnType<typeof useT>;

/**
 * The flat per-dimension pricing fields shared verbatim by
 * `CatalogModel` and `CatalogSku` — lets one line builder serve both
 * the legacy matrices and the SKU market.
 */
export type PricingFields = Pick<
  CatalogModel,
  | 'vendor'
  | 'input_per_mtok_usd'
  | 'output_per_mtok_usd'
  | 'cache_read_per_mtok_usd'
  | 'cache_write_per_mtok_usd'
  | 'cache_write_5m_per_mtok_usd'
  | 'cache_write_1h_per_mtok_usd'
>;

export function hasRateDiscount(rateMultiplier: number, baseRateMultiplier: number): boolean {
  return baseRateMultiplier > rateMultiplier + 0.0000001;
}

function isAnthropicVendor(vendor: string): boolean {
  return vendor.toLowerCase() === 'anthropic';
}

function isOpenAiVendor(vendor: string): boolean {
  return vendor.toLowerCase() === 'openai';
}

export function pricingLinesForModel(model: PricingFields): ReadonlyArray<{
  labelKey: I18nKey;
  value: number | null;
  notBilled?: boolean;
}> {
  const common = [
    { labelKey: 'pricing.rate.input', value: model.input_per_mtok_usd },
    { labelKey: 'pricing.rate.output', value: model.output_per_mtok_usd },
    { labelKey: 'pricing.rate.cache_read', value: model.cache_read_per_mtok_usd },
  ] satisfies ReadonlyArray<{ labelKey: I18nKey; value: number | null }>;
  if (isAnthropicVendor(model.vendor)) {
    return [
      ...common,
      {
        labelKey: 'pricing.rate.cache_write_5m',
        value: model.cache_write_5m_per_mtok_usd ?? model.cache_write_per_mtok_usd,
      },
      {
        labelKey: 'pricing.rate.cache_write_1h',
        value: model.cache_write_1h_per_mtok_usd ?? null,
      },
    ];
  }
  if (isOpenAiVendor(model.vendor)) {
    return [
      { labelKey: 'pricing.rate.input', value: model.input_per_mtok_usd },
      { labelKey: 'pricing.rate.cache_read', value: model.cache_read_per_mtok_usd },
      {
        labelKey: 'pricing.rate.cache_write',
        value: model.cache_write_per_mtok_usd,
        notBilled: (model.cache_write_per_mtok_usd ?? 0) <= 0,
      },
      { labelKey: 'pricing.rate.output', value: model.output_per_mtok_usd },
    ];
  }
  return [
    ...common,
    { labelKey: 'pricing.rate.cache_write', value: model.cache_write_per_mtok_usd },
  ];
}

/** One renderable token-card line from a billing document's rate card. */
export interface BillingTokenCardLine {
  readonly labelKey: I18nKey;
  readonly value: number;
  readonly notBilled: boolean;
}

/**
 * Canonical token-card order of `CatalogBillingTokenRates` — the
 * checklist's seven buckets, then the #138 image-input cell (ticket
 * tk_59ef52d2831fa277). Image input sits next to text input because it
 * is a subset of `inputTokens`, not a third independent counter.
 *
 * `image_input_per_mtok_usd` is optional on the wire; `null` / absent
 * still means "this dimension does not exist" and the row is omitted.
 */
const BILLING_TOKEN_CARD_BUCKETS: ReadonlyArray<{
  readonly field: keyof CatalogBillingTokenRates;
  readonly labelKey: I18nKey;
}> = [
  { field: 'input_per_mtok_usd', labelKey: 'pricing.rate.input' },
  { field: 'image_input_per_mtok_usd', labelKey: 'pricing.rate.image_input' },
  { field: 'output_per_mtok_usd', labelKey: 'pricing.rate.output' },
  { field: 'cache_read_per_mtok_usd', labelKey: 'pricing.rate.cache_read' },
  { field: 'cache_write_per_mtok_usd', labelKey: 'pricing.rate.cache_write' },
  { field: 'cache_write_5m_per_mtok_usd', labelKey: 'pricing.rate.cache_write_5m' },
  { field: 'cache_write_1h_per_mtok_usd', labelKey: 'pricing.rate.cache_write_1h' },
  { field: 'reasoning_per_mtok_usd', labelKey: 'pricing.rate.reasoning' },
];

/**
 * The token card of a billing DOCUMENT (checklist gap #4): up to seven
 * buckets, driven purely by the data —
 *
 *   - `null` (or a non-numeric wire value) = the dimension does not exist
 *     for this SKU → the row is not rendered at all;
 *   - `0` = the dimension exists and is deliberately free → the row
 *     renders "Not billed". The two must never collapse into each other.
 *
 * Deliberately NOT `pricingLinesForModel`: that builder serves the
 * legacy flat fields, where per-vendor masking (anthropic folds 5m into
 * the cache-write slot, openai reorders) papers over dimensions the wire
 * cannot express. The billing document expresses every dimension itself,
 * so the frontend stops owning vendor knowledge here.
 */
export function billingTokenCardLines(
  rates: CatalogBillingTokenRates,
): readonly BillingTokenCardLine[] {
  const lines: BillingTokenCardLine[] = [];
  for (const bucket of BILLING_TOKEN_CARD_BUCKETS) {
    const value = rates[bucket.field];
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      continue;
    }
    lines.push({ labelKey: bucket.labelKey, value, notBilled: value === 0 });
  }
  return lines;
}

/**
 * Per-unit price label for a unit-metered SKU line (#1706). The unit noun
 * dictionary is deliberately its own key set (`pricing.rate.unit.*`): the
 * `requests.unit_billing.unit.*` words are count nouns ("3 images"), not
 * the "per image" form a price line needs. A non-default variant rides as a
 * verbatim suffix — variant keys are operator-defined identifiers.
 */
function unitRateLabelKey(unit: UnitUsageUnit): I18nKey {
  switch (unit) {
    case 'call':
      return 'pricing.rate.unit.call';
    case 'second':
      return 'pricing.rate.unit.second';
    case 'character':
      return 'pricing.rate.unit.character';
    case 'image':
      return 'pricing.rate.unit.image';
  }
}

function unitRateLabel(t: TranslateFn, unit: UnitUsageUnit, variantKey: string): string {
  const base = t(unitRateLabelKey(unit));
  return variantKey === '' ? base : `${base} · ${variantKey}`;
}

/**
 * The prices cell of a unit-metered row (#1706). Empty rates means the model
 * is unit-metered but no price generation is currently effective: render
 * "pricing pending" — never the zero token dimensions, and never ¥0.00.
 */
export function UnitPricingCell({
  unitPricing,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
}: {
  readonly unitPricing: CatalogUnitPricing;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  if (unitPricing.rates.length === 0) {
    return (
      <p className="m-0 text-body text-ink-low" data-testid="pricing-unit-pending">
        {t('pricing.rate.unit.pending')}
      </p>
    );
  }
  return (
    <dl className="m-0 grid min-w-[200px] grid-cols-2 gap-x-4 gap-y-3">
      {unitPricing.rates.map((rate) => (
        <UnitPriceLine
          key={`${rate.unit}:${rate.variant_key}`}
          label={unitRateLabel(t, rate.unit, rate.variant_key)}
          rateUsdMicros={rate.rate_usd_micros_per_unit}
          rateMultiplier={rateMultiplier}
          baseRateMultiplier={baseRateMultiplier}
          cnyPerUsd={cnyPerUsd}
        />
      ))}
    </dl>
  );
}

/**
 * One per-unit price line. Unit prices are integer micro-USD and can sit far
 * below one cent (a per-character TTS rate is tens of micros), so they render
 * through the micros-precision CNY channel the Requests page uses — the
 * 2-decimal per-Mtok formatter would show ¥0.00. The multiplied USD amount
 * is passed through un-rounded: the ONE rounding happens on the CNY side
 * inside the formatter, so a 1-micro rate under a deep discount still shows
 * ¥0.000001 instead of collapsing to ¥0.000000 at an intermediate step.
 *
 * `unitSuffix` (tenant#117) rides after the effective price as " / second"
 * for billing-document tier rows, whose row label is the tier name and
 * would otherwise leave the unit of sale unstated. Callers that name the
 * unit in the label itself (the #1706 lines, the untiered unit line) omit
 * it, so every incumbent rendering is byte-identical.
 */
export function UnitPriceLine({
  label,
  rateUsdMicros,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
  unitSuffix,
}: {
  readonly label: string;
  readonly rateUsdMicros: number;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
  readonly unitSuffix?: string;
}): JSX.Element {
  const display = trimCnyTrailingZeros(
    formatUsdMicrosAsCny(rateUsdMicros * rateMultiplier, cnyPerUsd),
  );
  const original = hasRateDiscount(rateMultiplier, baseRateMultiplier)
    ? trimCnyTrailingZeros(formatUsdMicrosAsCny(rateUsdMicros * baseRateMultiplier, cnyPerUsd))
    : null;
  return (
    <div className="min-w-0">
      <dt className="mb-1 truncate text-mono-label text-ink-low">{label}</dt>
      <dd className="m-0 flex flex-col gap-0.5">
        <span className="font-mono text-body font-medium tabular-nums text-ink">
          {display}
          {unitSuffix !== undefined ? (
            <span className="font-normal text-ink-low">{` / ${unitSuffix}`}</span>
          ) : null}
        </span>
        {original !== null ? (
          <span className="font-mono text-mono-label tabular-nums text-ink-low line-through">
            {original}
          </span>
        ) : null}
      </dd>
    </div>
  );
}

/**
 * `decimals` (billing checklist): the card-wide CNY precision from
 * `cnyRateDecimalsForCard`. Defaults to 2 — the incumbent display — so
 * the legacy vendor matrices and flat fields render byte-identically;
 * only billing-document cards pass 4.
 */
export function PriceLine({
  label,
  value,
  notBilled = false,
  notBilledLabel,
  rateMultiplier,
  baseRateMultiplier,
  cnyPerUsd,
  decimals = 2,
}: {
  readonly label: string;
  readonly value: number | null;
  readonly notBilled?: boolean;
  readonly notBilledLabel: string;
  readonly rateMultiplier: number;
  readonly baseRateMultiplier: number;
  readonly cnyPerUsd: number | null;
  readonly decimals?: CnyRateDecimals;
}): JSX.Element {
  const display = notBilled
    ? notBilledLabel
    : value === null
      ? '—'
      : trimCnyTrailingZeros(formatCnyRateFromUsdRate(value * rateMultiplier, cnyPerUsd, decimals));
  const original =
    !notBilled && value !== null && hasRateDiscount(rateMultiplier, baseRateMultiplier)
      ? trimCnyTrailingZeros(
          formatCnyRateFromUsdRate(value * baseRateMultiplier, cnyPerUsd, decimals),
        )
      : null;
  return (
    <div className="min-w-0">
      <dt className="mb-1 truncate text-mono-label text-ink-low">{label}</dt>
      <dd className="m-0 flex flex-col gap-0.5">
        <span className="font-mono text-body font-medium tabular-nums text-ink">{display}</span>
        {original !== null ? (
          <span className="font-mono text-mono-label tabular-nums text-ink-low line-through">
            {original}
          </span>
        ) : null}
      </dd>
    </div>
  );
}
